import { useEffect, useMemo, useState, type UIEvent } from 'react';
import { Checkbox, FormControlLabel, Box, Typography, TextField, ToggleButtonGroup, ToggleButton } from '@mui/material';
import { FieldValue } from '../../../../../types/QueryBuilder';
import { useLanguage } from '../../../../../context/LanguageContext';

const normalizeForSearch = (value: string): string =>
    value.trim().toLowerCase().normalize('NFD').replace(/\p{Diacritic}/gu, '');

interface CheckboxEditorProps {
    ruleId: string;
    value: string | string[] | number | null;
    values: FieldValue[];
    onChange: (value: string | string[]) => void;
}

// The three states of a boolean opt-in field: unset means the criterion isn't filtered on at all.
const BOOLEAN_ACCEPT_VALUE = '1';
const BOOLEAN_REFUSE_VALUE = '0';
const BOOLEAN_NO_PREFERENCE_VALUE = '';

const CHECKBOX_OPTION_HEIGHT = 36;
const CHECKBOX_VISIBLE_ROWS = 5;
// Options are rendered a batch at a time and grown on scroll instead of all at once,
// since some fields (e.g. identifier/card lists) carry many options and rendering them all froze the UI.
const CHECKBOX_BATCH_SIZE = 5;
const SCROLL_LOAD_THRESHOLD_PX = 48;

function CheckboxEditor({ ruleId, value, values, onChange }: CheckboxEditorProps) {
    const { t } = useLanguage();
    const selectedValues = useMemo(
        () => (Array.isArray(value) ? value : (value ? [String(value)] : [])),
        [value]
    );
    const selectedSet = useMemo(() => new Set(selectedValues), [selectedValues]);
    const [visibleCount, setVisibleCount] = useState(CHECKBOX_BATCH_SIZE);
    const [search, setSearch] = useState('');

    useEffect(() => {
        setVisibleCount(CHECKBOX_BATCH_SIZE);
    }, [values]);

    useEffect(() => {
        setVisibleCount(CHECKBOX_BATCH_SIZE);
    }, [search]);

    const filteredValues = useMemo(() => {
        const query = normalizeForSearch(search);
        if (!query) return values;
        return values.filter(option => normalizeForSearch(option.label).includes(query));
    }, [values, search]);

    if (values.length === 0) {
        return (
            <ToggleButtonGroup
                size="small"
                exclusive
                value={String(value ?? BOOLEAN_NO_PREFERENCE_VALUE)}
                onChange={(_event, newValue) => onChange(newValue ?? BOOLEAN_NO_PREFERENCE_VALUE)}
            >
                <ToggleButton value={BOOLEAN_NO_PREFERENCE_VALUE}>
                    {t('pages.clients.queryBuilder.simple.optin.noPreference')}
                </ToggleButton>
                <ToggleButton value={BOOLEAN_ACCEPT_VALUE}>
                    {t('pages.clients.queryBuilder.simple.optin.accepts')}
                </ToggleButton>
                <ToggleButton value={BOOLEAN_REFUSE_VALUE}>
                    {t('pages.clients.queryBuilder.simple.optin.refuses')}
                </ToggleButton>
            </ToggleButtonGroup>
        );
    }

    const handleChange = (optionName: string, checked: boolean) => {
        let newValues: string[];
        if (checked) {
            newValues = [...selectedValues, optionName];
        } else {
            newValues = selectedValues.filter(v => v !== optionName);
        }
        onChange(newValues);
    };

    const handleScroll = (e: UIEvent<HTMLDivElement>) => {
        const { scrollTop, scrollHeight, clientHeight } = e.currentTarget;
        if (scrollHeight - scrollTop - clientHeight < SCROLL_LOAD_THRESHOLD_PX) {
            setVisibleCount(count => Math.min(count + CHECKBOX_BATCH_SIZE, filteredValues.length));
        }
    };

    const visibleValues = filteredValues.slice(0, visibleCount);

    return (
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5 }}>
            <Typography variant="caption" sx={{ color: '#495057', fontWeight: 600 }}>
                {t('pages.clients.queryBuilder.simple.selectedCount').replace('{count}', String(selectedValues.length))}
            </Typography>
            <TextField
                size="small"
                placeholder={t('pages.clients.queryBuilder.searchPlaceholder')}
                value={search}
                onChange={(e) => setSearch(e.target.value)}
            />
            <Box
                onScroll={handleScroll}
                sx={{
                    display: 'flex',
                    flexDirection: 'column',
                    maxHeight: CHECKBOX_OPTION_HEIGHT * CHECKBOX_VISIBLE_ROWS,
                    overflowY: 'auto',
                    border: '1px solid',
                    borderColor: 'divider',
                    borderRadius: 1,
                    bgcolor: 'background.paper',
                }}
            >
                {visibleValues.length === 0 ? (
                    <Typography variant="body2" sx={{ color: '#6c757d', px: 1, py: 1 }}>
                        {t('pages.clients.queryBuilder.simple.noOptionsFound')}
                    </Typography>
                ) : (
                    visibleValues.map((option, idx) => (
                        <FormControlLabel
                            key={`${ruleId}-checkbox-${idx}`}
                            sx={{ display: 'flex', height: CHECKBOX_OPTION_HEIGHT, minHeight: CHECKBOX_OPTION_HEIGHT, m: 0, px: 1 }}
                            control={
                                <Checkbox
                                    checked={selectedSet.has(option.name)}
                                    onChange={(e) => handleChange(option.name, e.target.checked)}
                                    size="small"
                                />
                            }
                            label={option.label}
                        />
                    ))
                )}
            </Box>
        </Box>
    );
};

export default CheckboxEditor;
